import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useEmployeePerformance,
  type PerformanceSummary,
} from './staff.api';

/** The first and last day of the month, as the range starts. */
function monthToDate() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const iso = (date: Date) => date.toISOString().slice(0, 10);
  return { startDate: iso(first), endDate: iso(last) };
}

/**
 * How quickly one person picks and packs.
 *
 * One call answers both; the original asked the same endpoint and read
 * `picking` and `packing` off the one response.
 */
export function PerformanceSheet({
  profileId,
  name,
  open,
  onOpenChange,
}: {
  profileId: string | null;
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [range, setRange] = useState(monthToDate);
  const performance = useEmployeePerformance(open ? profileId : null, range);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="truncate">{t('staff.performance')}</SheetTitle>
          <p className="text-muted-foreground truncate text-sm">{name}</p>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('staff.from')}</Label>
              <Input
                type="date"
                value={range.startDate}
                onChange={(event) =>
                  setRange((current) => ({
                    ...current,
                    startDate: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('staff.to')}</Label>
              <Input
                type="date"
                value={range.endDate}
                onChange={(event) =>
                  setRange((current) => ({
                    ...current,
                    endDate: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          {performance.isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full rounded-lg" />
              <div className="divide-y rounded-xl border">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-3 px-3 py-2.5"
                  >
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-5 w-12" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {performance.isError && (
            <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
              {t('staff.performanceFailed')}
            </p>
          )}

          {performance.isSuccess && (
            <div className="relative">
              <BusyOverlay show={performance.isFetching} />
              <Tabs
                defaultValue="picking"
                className={cn(
                  performance.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <TabsList className="w-full">
                  <TabsTrigger value="picking" className="flex-1">
                    {t('staff.picking')}
                  </TabsTrigger>
                  <TabsTrigger value="packing" className="flex-1">
                    {t('staff.packing')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="picking">
                  <Summary data={performance.data.picking} />
                </TabsContent>
                <TabsContent value="packing">
                  <Summary data={performance.data.packing} />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Summary({ data }: { data: PerformanceSummary | null }) {
  const { t } = useTranslation();

  const rows: [string, string][] = [
    [t('staff.orderCount'), formatNumber(data?.orderCount ?? 0)],
    [t('staff.itemsPerOrder'), formatNumber(data?.itemsPerOrder ?? 0)],
    [
      t('staff.itemsPerMinute'),
      // Null is not zero here: it means nothing had to be substituted, so
      // there is no per-minute rate to quote.
      data?.itemsPerMinute == null
        ? t('staff.allItems')
        : formatNumber(data.itemsPerMinute),
    ],
    [
      t('staff.averageTime'),
      `${formatNumber(data?.averageTime ?? 0)} ${t('staff.minutes')}`,
    ],
  ];

  return (
    <div className="divide-y rounded-xl border">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="flex items-center justify-between gap-3 px-3 py-2.5"
        >
          <span className="text-muted-foreground text-sm">{label}</span>
          <span className="font-semibold tabular-nums">{value}</span>
        </div>
      ))}
    </div>
  );
}
